import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

const Queue = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [takingConversation, setTakingConversation] = useState(null);
  const [userRole, setUserRole] = useState('agent');
  const navigate = useNavigate();

  useEffect(() => {
    getUserRole();
    fetchPendingConversations();
    fetchAgents();

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchPendingConversations();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const getUserRole = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'agent');
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  };

  const fetchAgents = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/v1/agents', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setAgents(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchPendingConversations = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      // Fetch only PENDING conversations
      const response = await fetch('/api/v1/conversations?status=pending&limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data.conversations || data.data || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching pending conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTakeConversation = async (conversationId) => {
    const token = localStorage.getItem('token');
    setTakingConversation(conversationId);

    try {
      const response = await fetch(`/api/v1/conversations/${conversationId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchPendingConversations();
        // Navigate to My Assignments
        navigate('/my-assignments', {
          state: { openConversationId: conversationId }
        });
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to take conversation');
      }
    } catch (err) {
      console.error('Error taking conversation:', err);
      alert('Failed to take conversation');
    } finally {
      setTakingConversation(null);
    }
  };

  const handleAssignToAgent = async () => {
    if (!selectedAgent || !selectedConversation) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/conversations/${selectedConversation}/assign-to`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ agent_id: selectedAgent })
      });

      if (response.ok) {
        await fetchPendingConversations();
        setShowAssignModal(false);
        setSelectedConversation(null);
        setSelectedAgent('');
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to assign conversation');
      }
    } catch (err) {
      console.error('Error assigning conversation:', err);
      alert('Failed to assign conversation');
    }
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const minutes = Math.floor((now - then) / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getWaitingTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const minutes = Math.floor((now - then) / 60000);
    
    if (minutes < 1) return { time: 'Just now', priority: 'low' };
    if (minutes < 5) return { time: `${minutes}m`, priority: 'low' };
    if (minutes < 15) return { time: `${minutes}m`, priority: 'medium' };
    if (minutes < 60) return { time: `${minutes}m`, priority: 'high' };
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { time: `${hours}h`, priority: 'critical' };
    return { time: `${Math.floor(hours / 24)}d`, priority: 'critical' };
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#28a745',
      'medium': '#ffc107',
      'high': '#ff9800',
      'critical': '#dc3545'
    };
    return colors[priority] || '#6c757d';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': '✓ New',
      'medium': '⚠️ Medium',
      'high': '🔥 High',
      'critical': '🚨 Critical'
    };
    return labels[priority] || priority;
  };

  return (
    <Layout>
      <div style={{ padding: '20px', minHeight: '100vh', backgroundColor: '#0a0e27' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0' }}>
              Message Queue ({conversations.length})
            </h1>
            <p style={{ margin: 0, color: '#6c757d' }}>
              Customers waiting for agent support - Auto-refresh every 10 seconds
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={fetchPendingConversations}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              🔄 Refresh Now
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{ 
            backgroundColor: 'rgba(26, 32, 56, 0.8)', 
            padding: '40px', 
            borderRadius: '10px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              margin: '0 auto 20px',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#9ca3af' }}>Loading pending conversations...</p>
          </div>
        ) : error ? (
          <div style={{
            backgroundColor: 'rgba(220, 53, 69, 0.2)',
            border: '1px solid rgba(220, 53, 69, 0.4)',
            color: '#f8d7da',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        ) : conversations.length === 0 ? (
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '60px 40px',
            textAlign: 'center',
            borderRadius: '10px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h2 style={{ margin: '0 0 10px 0', color: '#f9fafb' }}>Queue is Empty!</h2>
            <p style={{ color: '#9ca3af', margin: 0 }}>No customers are currently waiting for support</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '20px'
          }}>
            {conversations.map((conv) => {
              const waitInfo = getWaitingTime(conv.last_activity || conv.started_at);
              
              return (
                <div
                  key={conv.id}
                  style={{
                    backgroundColor: 'rgba(26, 32, 56, 0.8)',
                    border: `2px solid ${getPriorityColor(waitInfo.priority)}`,
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '10px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#f9fafb',
                          marginBottom: '5px',
                          wordBreak: 'break-all'
                        }}>
                          📞 {conv.phone_number || conv.session_id}
                        </div>
                        {conv.user_name && (
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#9ca3af',
                            marginBottom: '8px'
                          }}>
                            👤 {conv.user_name}
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '5px'
                      }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: getPriorityColor(waitInfo.priority),
                          color: 'white',
                          whiteSpace: 'nowrap'
                        }}>
                          {getPriorityLabel(waitInfo.priority)}
                        </span>
                        <span style={{
                          fontSize: '13px',
                          color: '#9ca3af',
                          fontWeight: '600'
                        }}>
                          ⏱️ {waitInfo.time}
                        </span>
                      </div>
                    </div>

                    <div style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      display: 'inline-block',
                      marginBottom: '10px'
                    }}>
                      ⏳ Pending Agent
                    </div>
                  </div>

                  {/* Last Agent Info */}
                  <div>
                    {conv.last_agent_name && (
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: 'rgba(220, 53, 69, 0.2)',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '16px' }}>🔄</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '11px', color: '#fca5a5', fontWeight: '600' }}>
                            LAST AGENT
                          </div>
                          <div style={{ fontSize: '13px', color: '#f8d7da', fontWeight: '600' }}>
                            {conv.last_agent_name}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleTakeConversation(conv.id)}
                        disabled={takingConversation === conv.id}
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: takingConversation === conv.id ? '#6c757d' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: takingConversation === conv.id ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}
                      >
                        {takingConversation === conv.id ? 'Taking...' : '✅ Take'}
                      </button>
                      
                      {(userRole === 'supervisor' || userRole === 'admin') && (
                        <button
                          onClick={() => {
                            setSelectedConversation(conv.id);
                            setShowAssignModal(true);
                          }}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}
                        >
                          👤 Assign
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid #e9ecef'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>Language</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#f9fafb' }}>
                        {conv.language === 'ar' ? '🇸🇦 Arabic' : '🇬🇧 English'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>Messages</div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#f9fafb' }}>
                        {conv.total_messages || 0}
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    fontSize: '13px', 
                    color: '#9ca3af',
                    marginTop: '10px'
                  }}>
                    Last activity: {getTimeSince(conv.last_activity || conv.started_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'rgba(26, 32, 56, 0.95)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(99, 102, 241, 0.3)'
            }}>
              <h2 style={{ marginTop: 0, color: '#f9fafb' }}>Assign to Agent</h2>
              <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
                Select an agent to assign this conversation to:
              </p>
              
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  backgroundColor: '#1a2038',
                  color: '#f9fafb'
                }}
              >
                <option value="">Select an agent...</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name} ({agent.email})
                  </option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedConversation(null);
                    setSelectedAgent('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignToAgent}
                  disabled={!selectedAgent}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: !selectedAgent ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: !selectedAgent ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
};

export default Queue;